import React, { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { createWhatsAppTemplate, getWhatsAppTemplates } from '../../../../../../../../services/whatsappIntegrationService';
import { useToast } from '../../../../../../../components/Toats';
import { useAuth } from '../../../../../../../../modules/auth';
import { LoadingSpan } from '../../../../../../../components/Loadings/loadingSpan';

interface WhatsAppTemplatesProps {
  companyId: string;
}

interface WhatsAppTemplateComponent {
  type: string;
  text?: string;
  format?: string;
  example?: {
    body_text?: string[][];
    header_text?: string[];
  };
  buttons?: Array<{
    type: string;
    text?: string;
    url?: string;
    example?: string[];
  }>;
}

interface WhatsAppTemplate {
  id: string;
  name: string;
  status: string;
  category: string;
  language: string;
  components?: WhatsAppTemplateComponent[];
  rejected_reason?: string;
}

interface WhatsAppTemplatesResponse {
  data: WhatsAppTemplate[];
  paging?: any;
}

const VALID_TEMPLATE_CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'] as const;
const VALID_COMPONENT_TYPES = ['BODY', 'HEADER', 'FOOTER', 'BUTTONS'] as const;

const normalizeTemplateName = (name: string) =>
  name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

const extractPlaceholderOrder = (text: string): string[] => {
  const order: string[] = [];
  const re = /\{\{(\d+)\}\}/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    order.push(match[1]);
  }
  return order;
};

const buildBodyExampleValues = (text: string, placeholderOrder: string[]): string[] => {
  const lowerText = text.toLowerCase();
  const looksLikeReportTemplate =
    /usina/.test(lowerText) && /referente|relat[oó]rio/.test(lowerText);

  return placeholderOrder.map((placeholder, index) => {
    const token = `{{${placeholder}}}`;
    const tokenIndex = lowerText.indexOf(token.toLowerCase());
    const context = tokenIndex >= 0
      ? lowerText.slice(Math.max(0, tokenIndex - 24), tokenIndex + token.length + 24)
      : '';

    if (/link|url|http|visualiz/.test(context) || (looksLikeReportTemplate && placeholder === '3')) {
      return 'https://dash.solarvision.app/relatorio/exemplo';
    }

    if (/usina|plant/.test(context) || (looksLikeReportTemplate && placeholder === '1')) {
      return 'Usina Solar Exemplo';
    }

    if (/m[eê]s|referente|data|per[ií]odo/.test(context) || (looksLikeReportTemplate && placeholder === '2')) {
      return '07/2026';
    }

    return `Exemplo ${index + 1}`;
  });
};

const enrichComponentWithExample = (component: WhatsAppTemplateComponent): WhatsAppTemplateComponent => {
  const type = (component.type || '').toUpperCase();
  const text = component.text || '';
  const placeholders = extractPlaceholderOrder(text);

  if (type === 'BODY' && placeholders.length > 0) {
    const existing = component.example?.body_text?.[0];
    if (Array.isArray(existing) && existing.length === placeholders.length) {
      return component;
    }

    return {
      ...component,
      type,
      example: {
        ...(component.example || {}),
        body_text: [buildBodyExampleValues(text, placeholders)]
      }
    };
  }

  if (type === 'HEADER' && placeholders.length > 0) {
    const existing = component.example?.header_text;
    if (Array.isArray(existing) && existing.length === placeholders.length) {
      return component;
    }

    return {
      ...component,
      type,
      example: {
        ...(component.example || {}),
        header_text: placeholders.map((_, index) => `Exemplo ${index + 1}`)
      }
    };
  }

  return {
    ...component,
    type
  };
};

const getTemplateStatusColor = (status?: string) => {
  switch (status?.toUpperCase()) {
    case 'APPROVED':
      return 'success';
    case 'PENDING':
      return 'warning';
    case 'REJECTED':
      return 'danger';
    default:
      return 'secondary';
  }
};

const getTemplateStatusText = (status?: string) => {
  switch (status?.toUpperCase()) {
    case 'APPROVED':
      return 'Aprovado';
    case 'PENDING':
      return 'Em análise';
    case 'REJECTED':
      return 'Rejeitado';
    default:
      return status || 'Desconhecido';
  }
};

const getComponentPreview = (component: WhatsAppTemplateComponent) => {
  const content = component.text || component.format;
  return content ? `${component.type}: ${content}` : component.type;
};

const templateSchema = Yup.object().shape({
  name: Yup.string()
    .required('Nome é obrigatório')
    .test(
      'valid-template-name',
      'Nome inválido após normalização. Use letras, números e espaços.',
      (value) => Boolean(normalizeTemplateName(value || ''))
    ),
  language: Yup.string().required('Idioma é obrigatório'),
  category: Yup.string()
    .required('Categoria é obrigatória')
    .oneOf([...VALID_TEMPLATE_CATEGORIES], 'Categoria inválida'),
  components: Yup.array().of(
    Yup.object().shape({
      type: Yup.string()
        .required()
        .oneOf([...VALID_COMPONENT_TYPES], 'Tipo de componente inválido'),
      text: Yup.string().when('type', {
        is: 'BODY',
        then: Yup.string().required('Texto do corpo é obrigatório')
      }),
      format: Yup.string().when('type', {
        is: 'HEADER',
        then: Yup.string().required('Formato do cabeçalho é obrigatório')
      }),
      buttons: Yup.array().when('type', {
        is: 'BUTTONS',
        then: Yup.array().min(1, 'Pelo menos um botão é obrigatório')
      })
    })
  ).min(1, 'Pelo menos um componente é obrigatório')
});

const getDefaultTemplateInitialValues = (companyName: string) => ({
  name: 'report_template',
  language: 'pt_BR',
  category: 'UTILITY',
  components: [
    {
      type: 'HEADER',
      text: 'RELATÓRIO MENSAL',
      format: 'TEXT'
    },
    {
      type: 'BODY',
      text: 'Olá, o relatório da usina {{1}} referente a {{2}} foi gerado com sucesso! Clique no link {{3}} para visualizá-lo.',
      example: {
        body_text: [[
          'Usina Solar Exemplo',
          '07/2026',
          'https://dash.solarvision.app/relatorio/exemplo'
        ]]
      }
    },
    {
      type: 'FOOTER',
      text: companyName || 'SOLAR VISION'
    }
  ]
});

export function WhatsAppTemplates({ companyId }: WhatsAppTemplatesProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const { toggleToast } = useToast();
  const { currentUser } = useAuth();
  const companyName = currentUser?.companies?.[0]?.corporateName || '';
  const initialValues = useMemo(
    () => getDefaultTemplateInitialValues(companyName),
    [companyName]
  );
  const {
    data: templatesResponse,
    isLoading: isLoadingTemplates,
    error: templatesError,
    refetch: refetchTemplates
  } = useQuery<WhatsAppTemplatesResponse>(
    ['whatsappTemplates', companyId],
    () => getWhatsAppTemplates(companyId),
    {
      enabled: !!companyId
    }
  );
  const templates = templatesResponse?.data || [];
  const hasTemplatesError = Boolean(templatesError);

  const createTemplateMutation = useMutation(
    createWhatsAppTemplate, {
    onSuccess: () => {
      queryClient.invalidateQueries(['whatsappTemplates', companyId]);
      setShowForm(false);
      formik.resetForm();
      toggleToast('Sucesso ao criar template', 'success');
    },
    onError: (error: any) => {
      console.error('Erro ao criar template:', error);
      const apiMessage =
        error?.response?.data?.message
        || error?.message
        || 'Erro ao criar template';
      toggleToast(apiMessage, 'error');
    }
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: templateSchema,
    onSubmit: (values) => {
      const normalizedTemplateData = {
        ...values,
        name: normalizeTemplateName(values.name),
        category: values.category.toUpperCase(),
        components: values.components.map((component) =>
          enrichComponentWithExample({
            ...component,
            type: component.type.toUpperCase()
          })
        ),
        companyId
      };
      createTemplateMutation.mutate(normalizedTemplateData);
    }
  });

  const addComponent = () => {
    const newComponents = [...formik.values.components, { type: 'BODY', text: '' }];
    formik.setFieldValue('components', newComponents);
  };

  const removeComponent = (index: number) => {
    const newComponents = formik.values.components.filter((_, i) => i !== index);
    formik.setFieldValue('components', newComponents);
  };

  return (
    <div>
      {/* <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">Templates de Mensagem</h5>
        
      </div> */}

      {showForm && (
        <div>
          <div className="d-flex justify-content-end mb-4">
            <button
                  onClick={() => setShowForm(!showForm)}
                  className="btn btn-primary btn-sm"
            >
              {showForm ? 'Cancelar' : 'Novo Template'}
            </button>
          </div>
          <form onSubmit={formik.handleSubmit}>
            <div className="card mb-4 bg-light">
              <div className="card-body">
                <div className='notice d-flex bg-light-info rounded border-info border border-dashed flex-shrink-0 p-6 mt-2 mb-8' style={{width: '100%'}}>  
                  <div className='d-flex flex-stack flex-grow-1 flex-wrap flex-md-nowrap'>
                    <div className='mb-3 mb-md-0 fw-bold'>
                      <div className='fs-6 text-info pe-7'>
                        Para cadastro de templates, segue as instruções abaixo:
                        <br />
                        {" "}1 -  É necessário ativar a integração com o WhatsApp na empresa.
                        <br />
                        {""}2 - O nome do template, linguagem e categoria utilizado no relatorio é o que está pre definido no sistema, e será usado para idenficação para envio de reletorio.
                        <br />
                        {" "}3 - Sobre os valores: (
                          {"{{1}}"} - nome da usina,
                          {"{{2}}"} - mês do relatório,
                          {"{{3}}"} - link do relatório),
                          são os valores que serão substituídos no template.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-4">
                    <label className="form-label fw-bold">Nome do Template</label>
                    <input
                      type="text"
                      disabled={true}
                      className="form-control"
                      {...formik.getFieldProps('name')}
                    />
                    {formik.touched.name && formik.errors.name && (
                      <div className="text-danger small">{formik.errors.name}</div>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-bold">Idioma</label>
                    <select
                      className="form-control"
                      {...formik.getFieldProps('language')}
                      disabled={true}
                    >
                      <option value="pt_BR">Português (BR)</option>
                      <option value="en_US">English (US)</option>
                      <option value="es_ES">Español</option>
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-bold">Categoria</label>
                    <select
                      className="form-control fw-bold"
                      {...formik.getFieldProps('category')}
                      disabled={true}
                    >
                      <option value="MARKETING">Marketing</option>
                      <option value="UTILITY">Utilitário</option>
                      <option value="AUTHENTICATION">Autenticação</option>
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="form-label fw-bold">Componentes</label>
                  {formik.values.components.map((component, index) => (
                    <div key={index} className="border rounded p-3 mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <select
                          className="form-control w-auto"
                          {...formik.getFieldProps(`components.${index}.type`)}
                        >
                          <option value="HEADER">Cabeçalho</option>
                          <option value="BODY">Corpo</option>
                          <option value="FOOTER">Rodapé</option>
                          {/* <option value="BUTTONS">Botões</option> */}
                        </select>

                        {formik.values.components.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeComponent(index)}
                            className="text-danger btn btn-sm btn-outline-danger text-hover-light"
                          >
                            Remover
                          </button>
                        )}
                      </div>

                      {component.type === 'BODY' && (
                        <textarea
                          className="form-control"
                          rows={3}
                          placeholder="Texto da mensagem"
                          {...formik.getFieldProps(`components.${index}.text`)}
                        />
                      )}

                      {component.type === 'HEADER' && (
                        <div>
                          <select
                            className="form-control mb-2"
                            {...formik.getFieldProps(`components.${index}.format`)}
                          >
                            <option value="TEXT">Texto</option>
                            {/* <option value="IMAGE">Imagem</option>
                            <option value="VIDEO">Vídeo</option>
                            <option value="DOCUMENT">Documento</option> */}
                          </select>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Conteúdo do cabeçalho"
                            {...formik.getFieldProps(`components.${index}.text`)}
                          />
                        </div>
                      )}

                      {component.type === 'FOOTER' && (
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Texto do rodapé"
                          {...formik.getFieldProps(`components.${index}.text`)}
                        />
                      )}

                      {component.type === 'BUTTONS' && (
                        <div>
                          <p className="small text-muted">Botões serão configurados aqui</p>
                          {/* Implementar botões depois */}
                        </div>
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addComponent}
                    className="btn btn-outline-primary btn-sm text-primary text-hover-light"
                  >
                    Adicionar Componente
                  </button>
                </div>

                <div className="d-flex justify-content-end mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={createTemplateMutation.isLoading}
                  >
                    {createTemplateMutation.isLoading ? 'Criando...' : 'Criar Template'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}

      {!showForm && 
      <div className="card bg-light">
        <div className="card-header border-0 pt-5">
          <div className="d-flex justify-content-between align-items-center w-100">
            <div>
              {isLoadingTemplates && (
              <div className="d-flex align-items-center text-muted">
                <span className="spinner-border spinner-border-sm me-2"></span>
                Carregando templates...
              </div>
              )}
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                onClick={() => refetchTemplates()}
                className="btn btn-sm btn-light btn-hover-primary text-primary text-hover-dark"
                disabled={isLoadingTemplates}
              >
                {isLoadingTemplates ? (
                  <span className="spinner-border spinner-border-sm"></span>
                ) : (
                  'Atualizar'
                )}
              </button>

              <button
                onClick={() => setShowForm(!showForm)}
                className="btn btn-primary btn-sm"
              >
                {showForm ? 'Cancelar' : 'Novo Template'}
              </button>
            </div>
          </div>
        </div>

        <div className="card-body pt-2">
          {hasTemplatesError && (
            <div className="alert alert-danger">
              Erro ao buscar templates: {templatesError instanceof Error ? templatesError.message : 'Erro desconhecido'}
            </div>
          )}

          {!isLoadingTemplates && !hasTemplatesError && templates.length === 0 && (
            <div className="alert alert-info mb-0">
              Nenhum template encontrado para esta empresa.
            </div>
          )}

          {!isLoadingTemplates && !hasTemplatesError && templates.length > 0 && (
            <div className="d-flex flex-column gap-4 bg-white">
              {templates.map((template) => (
                <div key={template.id} className="border rounded p-4">
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                    <div>
                      <h6 className="mb-1">{template.name}</h6>
                      <div className="text-muted small fw-bold">ID: {template.id}</div>
                    </div>
                    <span className={`badge badge-${getTemplateStatusColor(template.status)}`}>
                      {getTemplateStatusText(template.status)}
                    </span>
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <div className="text-muted small fw-bold">Categoria</div>
                      <div>{template.category || '-'}</div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-muted small fw-bold">Idioma</div>
                      <div>{template.language || '-'}</div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-muted small fw-bold">Componentes</div>
                      <div>{template.components?.length || 0}</div>
                    </div>
                  </div>

                  {template.components && template.components.length > 0 && (
                    <div className="mb-3">
                      <div className="text-muted small mb-2 fw-bold">Conteúdo</div>
                      <div className="d-flex flex-column gap-2">
                        {template.components.map((component, index) => (
                          <div key={`${template.id}-${component.type}-${index}`} className="small border rounded p-2 bg-light">
                            {getComponentPreview(component)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {template.rejected_reason && (
                    <div className="alert alert-danger mb-0">
                      Motivo da rejeição: {template.rejected_reason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>}
    </div>
  );
}